import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { subscribeToNewsletter } from '../../services/newsletter-proxy';

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    opacity: ${props => props.isVisible ? 1 : 0};
    visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ModalContent = styled.div`
    background: ${props => props.theme.body.background};
    border-radius: 12px;
    padding: 2.5rem;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    transform: ${props => props.isVisible ? 'scale(1)' : 'scale(0.9)'};
    transition: transform 0.3s ease;

    @media (max-width: 768px) {
        padding: 2rem;
        margin: 1rem;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: ${props => props.theme.body.text};
    opacity: 0.6;
    transition: opacity 0.2s ease;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        opacity: 1;
    }
`;

const ModalTitle = styled.h3`
    margin: 0 0 1rem 0;
    color: ${props => props.theme.body.headers};
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.3;
`;

const ModalDescription = styled.p`
    margin: 0 0 1.5rem 0;
    color: ${props => props.theme.body.text};
    font-size: 1rem;
    line-height: 1.6;
    opacity: 0.8;
`;

const NewsletterForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const EmailInput = styled.input`
    padding: 0.75rem 1rem;
    border: 2px solid ${props => props.theme.body.border || '#e5e5e5'};
    border-radius: 8px;
    font-size: 1rem;
    background: ${props => props.theme.body.background};
    color: ${props => props.theme.body.text};
    transition: border-color 0.2s ease;

    &:focus {
        outline: none;
        border-color: ${props => props.theme.body.accent || '#6366f1'};
    }

    &::placeholder {
        color: ${props => props.theme.body.text};
        opacity: 0.5;
    }
`;

const SubmitButton = styled.button`
    padding: 0.75rem 1.5rem;
    background: ${props => props.theme.body.headers};
    color: ${props => props.theme.body.background};
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
`;

const SuccessMessage = styled.div`
    text-align: center;
    color: #4a5568;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    margin-top: 1rem;
    font-size: 1.1rem;
    line-height: 1.6;
    
    .title {
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: #2d3748;
    }
    
    .subtitle {
        font-weight: 400;
        font-size: 0.95rem;
        opacity: 0.9;
    }
`;

const ErrorMessage = styled.div`
    text-align: center;
    color: #4a5568;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    margin-top: 1rem;
    font-size: 0.9rem;
    line-height: 1.4;
`;

const NewsletterModal = ({ isOpen, onClose }) => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || isSubmitting) return;

        setIsSubmitting(true);
        setError('');
        
        try {
            const result = await subscribeToNewsletter(email);
            
            if (result.success) {
                // Marcar como suscrito en localStorage
                localStorage.setItem('newsletter_subscribed', 'true');
                localStorage.setItem('newsletter_email', email);
                
                setIsSuccess(true);
                
                // Cerrar modal después de 3 segundos
                setTimeout(() => {
                    onClose();
                    setIsSuccess(false);
                    setEmail('');
                    setError('');
                }, 3000);
            } else {
                setError(result.error || 'Error al suscribirse. Inténtalo de nuevo.');
            }
            
        } catch (error) {
            console.error('Error subscribing to newsletter:', error);
            setError('Error de conexión. Verifica tu internet e inténtalo de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay isVisible={isOpen} onClick={handleOverlayClick}>
            <ModalContent isVisible={isOpen}>
                <CloseButton onClick={onClose}>×</CloseButton>
                
                {!isSuccess ? (
                    <>
                        <ModalTitle>¡Mantente al día con las reflexiones filosóficas!</ModalTitle>
                        <ModalDescription>
                            Recibe los nuevos artículos sobre filosofía, ética, estética y las grandes preguntas de la existencia directamente en tu correo.
                        </ModalDescription>
                        
                        <NewsletterForm onSubmit={handleSubmit}>
                            <EmailInput
                                type="email"
                                placeholder="tu@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={isSubmitting}
                            />
                            <SubmitButton type="submit" disabled={isSubmitting || !email}>
                                {isSubmitting ? 'Suscribiendo...' : 'Suscribirse'}
                            </SubmitButton>
                            
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                        </NewsletterForm>
                    </>
                ) : (
                    <SuccessMessage>
                        <div className="title">¡Suscripción exitosa!</div>
                        <div className="subtitle">
                            Pronto recibirás contenido filosófico inútil.
                        </div>
                    </SuccessMessage>
                )}
            </ModalContent>
        </ModalOverlay>
    );
};

export default NewsletterModal;